import {
    type QueryClient,
    type QueryFunction,
    type QueryFunctionContext,
    type QueryKey,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

export const POLL_INTERVAL_MS = 2000;

interface PolledListOptions<Item> {
    queryKey: QueryKey;
    queryFn: QueryFunction<Item[]>;
    // E.g. a sync.
    isRunning: (item: Item) => boolean;
    // When an item stops running, also by leaving the list: fetches again what it changed.
    onFinished: () => void;
}

// The options of a list that's fetched again every two seconds while one of its items is running.
export const pollWhileRunning = <Item extends { id: string }>(
    queryClient: QueryClient,
    { queryKey, queryFn, isRunning, onFinished }: PolledListOptions<Item>
) => ({
    queryKey,
    queryFn: async (context: QueryFunctionContext) => {
        const previous = queryClient.getQueryData<Item[]>(queryKey) ?? [];
        const items = await queryFn(context);
        const hasFinished = previous.some(
            (item) => isRunning(item) && !items.some((current) => current.id === item.id && isRunning(current))
        );
        if (hasFinished) onFinished();
        return items;
    },
    refetchInterval: (query: { state: { data?: Item[] } }) =>
        query.state.data?.some(isRunning) ? POLL_INTERVAL_MS : false,
});

export const usePolledList = <Item extends { id: string }>(options: PolledListOptions<Item>) => {
    const queryClient = useQueryClient();
    return useQuery(pollWhileRunning(queryClient, options));
};
